import { useRef, type ReactNode } from 'react'
import { Button, Flex, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { rememberKey, type KeyHolder } from './idempotencyHolder'

const TAP_HEIGHT = 52

interface ArmedProps {
  summary: ReactNode
  confirmLabel: string
  running: boolean
  onConfirm: (idempotencyKey: string) => void
  onCancel: () => void
  idempotencyKey: string
}

/**
 * Tasdiq bosqichi — VA `Idempotency-Key` NING UMRI.
 *
 * ⚠️ Kalit `ConfirmAction` da yashaydi va `seed` ga bog'langan —
 * `ArmedConfirm` ichida EMAS. Sabab o'lchangan (2026-08-20):
 * `ArmedConfirm` `armed` yolg'on bo'lganda unmount bo'ladi, chaqiruvchi
 * esa uni HAR natijada yopadi, tarmoq xatosida ham. Kalit ichkarida
 * bo'lsa qayta urinish YANGI kalit bilan ketardi va server uni ikkinchi
 * to'lov deb qabul qilardi. Endi:
 *
 *   · tugma qayta bosilsa yoki tarmoq uzilib qayta urinilsa — O'SHA
 *     kalit, ya'ni server ikkinchi so'rovni TAKROR deb taniydi va
 *     ikkinchi marta pul yechmaydi;
 *   · summa yoki abonent o'zgarsa — `seed` o'zgaradi, React komponentni
 *     qayta mount qiladi va YANGI kalit yasaladi, ya'ni haqiqiy ikkinchi
 *     to'lov "dublikat" deb jimgina rad etilmaydi.
 *
 * Ikkala tomon ham kerak. `features/agent/agentApi.ts` da kalit so'rov
 * ichida yasaladi — u ikkinchi shartni bajaradi, birinchisini esa yo'q.
 */
function ArmedConfirm({
  summary,
  confirmLabel,
  running,
  onConfirm,
  onCancel,
  idempotencyKey,
}: ArmedProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  return (
    <Flex vertical gap={12}>
      {/*
        ⚠️ XULOSA TUGMANING USTIDA va bu joylashuv ATAYLAB.

        Ikki talab qarama-qarshi: pul amali qaytarib bo'lmaydi (tasdiq
        kerak), kassir esa kuniga o'nlab marta shu ishni qiladi (tasdiq
        sekinlashtirmasin). Alohida tasdiq oynasi ikkalasini ham
        buzardi — u ekranni yopadi, kontekstni yo'qotadi va telefonda
        yana bir marta "OK" ni izlashga majbur qiladi.

        Shuning uchun tasdiq SHU YERDA, bir joyda qoladi va bitta
        qo'shimcha bosishga tushadi. Xulosa bloki tugmani PASTGA suradi,
        ya'ni tasodifiy ikki marta bosishning ikkinchisi tasdiq tugmasiga
        TUSHMAYDI — u xulosa matniga tegadi va hech narsa qilmaydi.
      */}
      <div
        style={{
          background: token.colorFillTertiary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: 12,
        }}
      >
        {summary}
        <Typography.Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          {t('cashdesk.irreversible')}
        </Typography.Text>
      </div>

      <Button
        type="primary"
        block
        size="large"
        loading={running}
        disabled={running}
        style={{ height: TAP_HEIGHT, fontWeight: 600 }}
        onClick={() => onConfirm(idempotencyKey)}
      >
        {confirmLabel}
      </Button>

      <Button block type="text" disabled={running} onClick={onCancel}>
        {t('common.cancel')}
      </Button>
    </Flex>
  )
}

interface ConfirmActionProps {
  /** Birinchi bosqichdagi tugma matni */
  label: string
  /** Ikkinchi bosqichdagi tugma matni — nima bo'lishini AYNAN aytadi */
  confirmLabel: string
  /** Tasdiq bosqichida ko'rsatiladigan xulosa: kimga, qancha */
  summary: ReactNode
  disabled: boolean
  running: boolean
  /**
   * Nishon + summa barmog'i. O'zgarganda YANGI idempotentlik kaliti
   * yasaladi — ya'ni haqiqiy ikkinchi to'lov dublikat deb rad etilmaydi.
   */
  seed: string
  keyPrefix: string
  /**
   * Bosqich BOSHQARILADI, komponentning ichida emas.
   *
   * ⚠️ Sabab natijada: server rad javobi (mablag' yetmadi) kelganda
   * bosqich YOPILISHI kerak — kassir summani tuzatadi. Muvaffaqiyatda
   * esa amal tugadi va tasdiq tugmasi ekranda TURMASLIGI kerak, aks
   * holda u ikkinchi to'lovga taklif bo'lib ko'rinardi. Ichki holat
   * bilan ikkalasini ham chaqiruvchi boshqara olmasdi.
   */
  armed: boolean
  onArm: () => void
  onCancel: () => void
  onConfirm: (idempotencyKey: string) => void
}

/** Ikki bosqichli amal tugmasi: bosish → xulosa → tasdiq. */
export function ConfirmAction({
  label,
  confirmLabel,
  summary,
  disabled,
  running,
  seed,
  keyPrefix,
  armed,
  onArm,
  onCancel,
  onConfirm,
}: ConfirmActionProps) {
  // ⚠️ `useRef`, `useState` EMAS: bu yerda qayta render KERAK EMAS va
  //    `useState` ning boshlang'ich qiymati seed o'zgarganda yangilanmaydi.
  const held = useRef<KeyHolder | null>(null)
  held.current = rememberKey(held.current, seed, () => newIdempotencyKey(keyPrefix))
  const idempotencyKey = held.current.key

  if (!armed) {
    return (
      <Button
        type="primary"
        block
        size="large"
        disabled={disabled}
        style={{ height: TAP_HEIGHT, fontWeight: 600 }}
        onClick={onArm}
      >
        {label}
      </Button>
    )
  }

  return (
    <ArmedConfirm
      idempotencyKey={idempotencyKey}
      summary={summary}
      confirmLabel={confirmLabel}
      running={running}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}
